import React, { useEffect, useRef, useState } from 'react';
import { Clock, CheckCircle, Eye, TrendingUp } from 'lucide-react';
import { videosAPI } from '../services/api';

// Les médias enregistrés en base peuvent être des URLs complètes ou des chemins /api/uploads/...
const mediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
};

// Une vue = la carte est visible à l'écran, au maximum une fois par session et par cours
const trackViewOnce = (courseId) => {
  const key = `video_view_${courseId}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch (e) {
    // sessionStorage indisponible : on compte quand même la vue
  }
  videosAPI.trackView(courseId).catch(() => {});
};

const VideoCourseCard = ({ course, onBuy, buying = false, rank = null, showStats = false, trackViews = false }) => {
  const cardRef = useRef(null);
  const [videoFailed, setVideoFailed] = useState(false);

  const video = mediaUrl(course.videos?.[0]);
  const image = mediaUrl(course.images?.[0]);

  useEffect(() => {
    if (!trackViews || !course.id || typeof IntersectionObserver === 'undefined') return undefined;
    const node = cardRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          trackViewOnce(course.id);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [trackViews, course.id]);

  return (
    <div
      ref={cardRef}
      className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-rose-100 shadow-sm hover:shadow-lg transition-all h-full"
      data-testid={`video-course-card-${course.id}`}
    >
      <div className="relative aspect-video bg-rose-50 overflow-hidden">
        {video && !videoFailed ? (
          <video
            src={video}
            poster={image || undefined}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            onError={() => setVideoFailed(true)}
            className="w-full h-full object-cover"
          />
        ) : image ? (
          <img src={image} alt={course.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-rose-100 to-rose-50" />
        )}

        {rank && (
          <div className="absolute top-2 left-2 w-8 h-8 rounded-full bg-white/90 text-[#D4627F] font-bold flex items-center justify-center shadow">
            {rank}
          </div>
        )}
      </div>

      <div className="flex flex-col flex-1 p-3 sm:p-4">
        <h3 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2 mb-1">{course.title}</h3>
        <p className="text-xs sm:text-sm text-gray-600 line-clamp-3 mb-3">{course.description}</p>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs text-gray-500 mb-3">
          {course.duration && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {course.duration}
            </span>
          )}
          {course.certificate && (
            <span className="flex items-center gap-1 text-[#D4627F]">
              <CheckCircle className="w-3 h-3" />
              Certificat
            </span>
          )}
          {showStats && (
            <>
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {course.views || 0} vues
              </span>
              <span className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                {course.conversion_rate || 0}% d'achat
              </span>
            </>
          )}
        </div>

        <div className="mt-auto flex items-center justify-between pt-3 border-t border-rose-100">
          <p className="text-lg sm:text-xl font-bold text-gray-900">
            {course.price} <span className="text-xs font-normal text-gray-500">CHF</span>
          </p>
          <button
            onClick={() => onBuy && onBuy(course)}
            disabled={buying}
            className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#D4627F] text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-[#B84A66] transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid={`buy-video-course-${course.id}`}
          >
            {buying ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              'Acheter'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoCourseCard;
